/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, styled, Typography } from '@mui/material';

import { InputValidationError, StrengthTargetAssign } from '../../../types';

import ErrorSvg from '../../../assets/images/svg/ic_error_ifo.svg';
import DeleteSvgIcon from '../../../assets/SvgIcon/DeleteSvgIcon';
import Input from '../../TextField/Input';

import './TargetOperationDialog.css';

const ContainerContent = styled('div')`
  margin-top: 10px;
  background-color: rgba(0, 0, 0, 0.2);
  padding: 0 10px 10px 10px;
  border-radius: 4px;
`;

const Header = styled('div')`
  display: flex;
  align-items: center;
  height: 32px;
  color: white;
`;

const SpanRequire = styled('span')`
  color: ${({ theme }) => theme.color.highlight};
`;

const Error = styled(Box)`
  display: flex;
  flex-flow: row wrap;
  justify-content: center;
  align-items: center;
  & > span {
    color: #ff6b00;
    font-size: 0.75rem;
    line-height: 1.25rem;
  }
`;

const ContainerDetails = styled('div')`
  margin: 10px 0;
  height: 58px;
`;

const Title = styled(Typography)(({ theme }) => ({
  ...theme.externalTypography.body3,
  color: theme.color.box_bbg,
}));

const ContainerDelete = styled('div')`
  height: 32px;
  width: 32px;
  margin-left: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: 0.3s;
  border-radius: 5px;
  cursor: pointer;
  :hover {
    background-color: rgba(0, 0, 0, 0.2);
  }
`;

interface StrengthCardProps {
  onChange: (index: number, data: StrengthTargetAssign) => void;
  data: StrengthTargetAssign;
  index: number;
  onDeleteCard: (index: number) => void;
}

const StrengthCard: React.FC<StrengthCardProps> = ({
  index,
  onChange,
  data,
  onDeleteCard,
}) => {
  const { t } = useTranslation('project-setting');

  const [strengthName, setStrengthName] = useState(data.name ?? '');
  const [strengthNameError, setStrengthNameError] =
    useState<InputValidationError>({ isError: false, message: '' });

  const [mpa, setMpa] = useState<string>(
    data.mpa !== undefined ? String(data.mpa) : '',
  );
  const [mpaError, setMpaError] = useState<InputValidationError>({
    isError: false,
    message: '',
  });

  useEffect(() => {
    if (data.name && data.name !== strengthName) {
      setStrengthName(data.name);
    }
    if (data.mpa !== undefined && String(data.mpa) !== mpa) {
      setMpa(String(data.mpa));
    }
  }, [data]);

  useEffect(() => {
    onChange(index, {
      id: data.id,
      name: strengthName,
      mpa: mpa !== '' ? Number(mpa) : undefined,
    });
  }, [strengthName, mpa]);

  const handleStrengthNameBlur = () => {
    if (strengthName === '') {
      setStrengthNameError({
        isError: true,
        message: t('errorJson.inputIsEmpty'),
      });
    } else {
      setStrengthNameError({ isError: false, message: '' });
    }
  };

  const handleMpaBlur = () => {
    if (mpa === '') {
      setMpaError({ isError: true, message: t('errorJson.inputIsEmpty') });
    } else if (isNaN(Number(mpa))) {
      setMpaError({ isError: true, message: t('errorJson.invalidNumber') });
    } else {
      setMpaError({ isError: false, message: '' });
    }
  };

  return (
    <ContainerContent>
      <Header>
        <Typography variant="body2">{t('custom-strength')}</Typography>
        <ContainerDelete onClick={() => onDeleteCard(index)}>
          <DeleteSvgIcon />
        </ContainerDelete>
      </Header>

      <ContainerDetails>
        <Title>
          {t('Target Name')}
          <SpanRequire>*</SpanRequire>
        </Title>
        <Input
          name={t('target-name')}
          value={strengthName}
          onChange={(e) => setStrengthName(e.target.value)}
          onBlur={handleStrengthNameBlur}
          error={strengthNameError.isError}
          errorMessage={strengthNameError.message}
          autoComplete="off"
        />
      </ContainerDetails>

      <ContainerDetails>
        <Title>
          {t('strength')} (MPa)
          <SpanRequire>*</SpanRequire>
        </Title>
        <Input
          name={t('strength')}
          value={mpa}
          onChange={(e) => setMpa(e.target.value)}
          onBlur={handleMpaBlur}
          error={mpaError.isError}
          errorMessage={mpaError.message}
          autoComplete="off"
        />
      </ContainerDetails>
    </ContainerContent>
  );
};

export default StrengthCard;
